import { Component, OnInit, Input } from '@angular/core';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, defaultIfEmpty, retry, switchMap, tap, timeout } from 'rxjs/operators';
import { ActivatedRoute, Data, Router } from '@angular/router';
import * as Sentry from '@sentry/browser';

import { Lead } from '../../../../shared/lead';
import { BrandService } from '../../../../services/brand.service';
import { FruHttpService } from '../../../../services/fru-http.service';
import { SelectedLendersService } from '../../../../services/selected-lenders.service';
import { ConsentAttemptTelemetryService } from '../../../../services/consent-attempt-telemetry.service';
import { environment } from '../../environments/environment';
import { OfficeStatus } from '../../../../shared/office-status';
import { SelectedResponse, SelectedList } from '../../../../shared/selected-response.model';

@Component({
    selector: 'one-to-one-consent',
    templateUrl: './one-to-one-consent.component.html',
    styleUrls: ['./one-to-one-consent.component.scss'],
    standalone: false
})
export class OneToOneConsentComponent implements OnInit {
  @Input() lead: Lead;
  public officeStatus: OfficeStatus;
  public msg: string;
  public currentYear: number = 2023;
  public encryptedLeadId: string;
  public selectedEnabled: boolean = false;
  public loadErrorMessage: string = '';

  availableSelected$: Observable<SelectedResponse>;

  // FRU-22871 diagnostics: guards so a single lead-load failure is recorded once per attempt
  // (the catchError and the null-response path can both observe the same failure).
  private leadLoadFailureSent = false;
  private leadLoadResponseReceived = false;

  constructor(private fruHttp: FruHttpService,
    private selectedLendersService: SelectedLendersService,
    private route: ActivatedRoute,
    private router: Router,
    public brandService: BrandService,
    private consentTelemetry: ConsentAttemptTelemetryService)
  {
    this.availableSelected$ = selectedLendersService.selectedLenders$;
  }

  ngOnInit() {
    let d = new Date();
    this.currentYear = d.getFullYear();

    this.route.queryParams.subscribe(params => {
      this.msg = (params.utm_medium == 'click-expired')
        ? 'please verify your information to get updated rates.'
        : 'as we discussed in my message, please verify the information below is still accurate. Thank you!';

      this.fruHttp
        .getOfficeStatus(params.utm_source || 'consumer_campaign')
        .subscribe(officeStatus => this.officeStatus = officeStatus)
    });


    //The router reuses this component when the stale-link redirect swaps the
    //lead id, so the id must come from paramMap: the snapshot would keep
    //pointing at the old lead
    this.route.paramMap.pipe(
      tap(paramMap => {
        this.lead = undefined;
        this.loadErrorMessage = '';
        this.leadLoadFailureSent = false;
        this.leadLoadResponseReceived = false;
        this.encryptedLeadId = paramMap.get('id');
        if (this.encryptedLeadId) {
          Sentry.setUser({ id: this.encryptedLeadId });
          //FRU-22871: a (restored) attempt per encrypted lead id; a stale-link
          //redirect to a new id starts a fresh attempt. Diagnostics only.
          const src = String(this.route.snapshot.queryParams.src || '').toLowerCase();
          this.consentTelemetry.startAttempt(this.encryptedLeadId, ['sms', 'email'].includes(src) ? src : 'web');
          this.consentTelemetry.record(this.encryptedLeadId, { stage: 'application_started', outcome: 'success' });
        } else {
          Sentry.setUser(null);
        }
      }),
      switchMap(() => this.getLead())
    ).subscribe(lead => this.handleLeadResponse(lead));
  }

  getLead(): Observable<Lead> {
    const id = this.encryptedLeadId;
    const leadType: string = this.route.snapshot.data.type || '';

    if (!id) {
      this.redirectToMainForm();
      return EMPTY;
    }

    this.consentTelemetry.record(id, { stage: 'lead_load_started', outcome: 'success' });

    return this.fruHttp.getLead(id, leadType, 'consumer_campaign', true).pipe(
      //FRU-22871: an EMPTY completion (status 0 in FruHttpService.getLead) emits nothing, so
      //this marks that a real response arrived and lets the null path name the failure
      tap(() => { this.leadLoadResponseReceived = true; }),
      timeout(8000),
      retry(1),
      defaultIfEmpty(null),
      catchError(err => {
        this.recordLeadLoadFailure(err);
        return of(null);
      })
    );
  }

  //FRU-22871 diagnostics only — never changes what the consumer sees.
  private recordLeadLoadFailure(error: any): void {
    this.leadLoadFailureSent = true;
    const isTimeout = !!(error && (error.name === 'TimeoutError' || /timeout/i.test(String(error && error.message || ''))));
    const status = (error && typeof error.status === 'number') ? error.status : 0;
    this.consentTelemetry.record(this.encryptedLeadId, {
      stage: 'lead_load_failed',
      outcome: 'failure',
      reason: isTimeout ? 'timeout' : 'http_error',
      httpStatus: status,
    });
  }

  handleLeadResponse(lead: Lead) {
    if (lead === null) {
      //FRU-22871: null without a recorded error means the request completed EMPTY (status 0 /
      //network) or the backend answered nothing — record it once, then keep the existing redirect.
      if (!this.leadLoadFailureSent) {
        this.consentTelemetry.record(this.encryptedLeadId, {
          stage: 'lead_load_failed',
          outcome: 'failure',
          reason: this.leadLoadResponseReceived ? 'unknown' : 'network',
          httpStatus: 0,
        });
      }
      this.redirectToMainForm();
      return;
    }

    if (lead.status === 'error') {
      this.consentTelemetry.record(this.encryptedLeadId, {
        stage: 'lead_load_failed',
        outcome: 'failure',
        reason: lead.reason === 'ambiguous_consent_link' ? 'ambiguous_link' : (lead.reason || 'error'),
      });
      this.loadErrorMessage = 'We could not safely load this consent link. Please refresh the link and try again.';
      return;
    }

    //A stale consent link points at an old request: swap the URL to the
    //current lead before the form is shown or submitted, so Jornaya and
    //TrustedForm tokens are generated for the current request
    if (lead.status === 'redirect' && lead.reason === 'stale_consent_link' && lead.leadId) {
      this.consentTelemetry.record(this.encryptedLeadId, {
        stage: 'stale_link_redirect',
        outcome: 'unknown',
        reason: 'stale_consent_link',
        requestId: lead.leadId,
        originalRequestId: this.encryptedLeadId,
        metadata: { original_encrypted_lead_id: this.encryptedLeadId },
      });
      this.router.navigate(['/consent', lead.leadId], { queryParams: this.route.snapshot.queryParams });
      return;
    }

    this.lead = lead;
    let params: any = this.route.snapshot.queryParams;
    if (params.utm_source) {
      this.lead.adnetwork = params.utm_source;
    }
    this.lead.medium = params.utm_medium || '';
    this.lead.campaign = this.encryptedLeadId; // Store the encrypted id
    this.lead.keyword = params.utm_keyword || '';
    this.lead.content = params.utm_content || '';
    this.lead.network = params.network || '';
    this.lead.gclid = params.gclid || '';
    this.lead.clickid = params.msclkid || '';

    this.consentTelemetry.record(this.encryptedLeadId, { stage: 'lead_load_success', outcome: 'success' });
    this.consentTelemetry.record(this.encryptedLeadId, { stage: 'form_ready', outcome: 'success' });

    //Selected lenders are only fetched once the lead is known current, so a
    //stale link never fetches them for the old lead id
    if (this.selectedEnabled) {
      this.selectedLendersService.getList(this.encryptedLeadId, 0, 'consent-form');
    }
  }

  redirectToMainForm() {
    if (environment.production) {
      this.fruHttp.redirectMainForm();
    } else {
      document.write("Invalid URL. Normally I'd redirect to the normal form, but this is a development site.");
    }
  }
}
